import React, { useCallback, useEffect, useState } from 'react';
import { EnvelopeIcon, XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import settingsService from '../../services/settingsService';
import { errText } from '../../services/scheduleService';

const SECURITY = [
  { value: 'starttls', label: 'STARTTLS (usually port 587)' },
  { value: 'tls', label: 'TLS / SSL (usually port 465)' },
  { value: 'none', label: 'None — local relay only' },
];
const PURPOSE = { test: 'Test', patient_onboarding: 'Patient onboarding' };

const toForm = (c) => ({
  is_enabled: !!c?.is_enabled,
  smtp_host: c?.smtp_host || '',
  smtp_port: c?.smtp_port ?? 587,
  security: c?.security || 'starttls',
  username: c?.username || '',
  password: '',
  from_name: c?.from_name || '',
  from_address: c?.from_address || '',
  reply_to: c?.reply_to || '',
});

/**
 * Email — the SMTP server the practice sends through (patient onboarding links
 * first; statements and reminders later). The password is write-only.
 */
const EmailSettings = () => {
  const [data, setData] = useState(null); // { config, presets, encryption_ready }
  const [log, setLog] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [d, l] = await Promise.all([settingsService.email(), settingsService.emailLog()]);
      setData(d);
      setLog(l);
      setError('');
    } catch (e) {
      setError(errText(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const c = data?.config;
  const configured = !!(c?.smtp_host && c?.from_address);

  return (
    <section className="mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-4 px-5 py-4">
        <span className="grid h-11 w-11 place-items-center rounded-xl bg-teal-50 text-brand-teal"><EnvelopeIcon className="h-6 w-6" /></span>
        <div className="min-w-0 flex-1">
          <h2 className="font-bold text-slate-800">Email</h2>
          <p className="text-sm text-slate-500">
            {data === null
              ? 'Checking…'
              : !configured
                ? 'Not set up. OmniPrac sends patient onboarding links through your own email account or provider.'
                : <>Sending as <strong>{c.from_name ? `${c.from_name} <${c.from_address}>` : c.from_address}</strong> via {c.smtp_host}:{c.smtp_port}.</>}
          </p>
          {c?.last_test_at && (
            <p className={`mt-0.5 flex items-center gap-1 text-xs ${c.last_test_ok ? 'text-green-700' : 'text-red-600'}`}>
              {c.last_test_ok ? <CheckCircleIcon className="h-4 w-4" /> : <ExclamationTriangleIcon className="h-4 w-4" />}
              Last test {new Date(c.last_test_at).toLocaleString('en-ZA')}: {c.last_test_ok ? 'delivered to the server' : c.last_test_error}
            </p>
          )}
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${c?.is_enabled ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}>
          {c?.is_enabled ? 'On' : 'Off'}
        </span>
        <button onClick={() => setOpen(true)} disabled={!data} className="rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50">
          Configure email
        </button>
      </div>
      {error && <p className="border-t border-red-100 bg-red-50 px-5 py-2 text-sm text-red-700">{error}</p>}

      {log.length > 0 && (
        <div className="border-t border-slate-100 px-5 py-3">
          <div className="mb-1 flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400">Recent emails</h3>
            <button onClick={load} title="Refresh" className="rounded p-1 text-slate-400 hover:bg-slate-100"><ArrowPathIcon className="h-4 w-4" /></button>
          </div>
          <ul className="divide-y divide-slate-100 text-sm">
            {log.slice(0, 8).map((e) => (
              <li key={e.email_id} className="flex flex-wrap items-center gap-2 py-1.5">
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${e.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{e.status}</span>
                <span className="text-slate-700">{e.to_address}</span>
                <span className="text-xs text-slate-400">{PURPOSE[e.purpose] || e.purpose} · {new Date(e.created_at).toLocaleString('en-ZA')}{e.sent_by ? ` · ${e.sent_by}` : ''}</span>
                {e.error && <span className="w-full text-xs text-red-600">{e.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {open && data && <EmailModal data={data} onClose={() => setOpen(false)} onChanged={load} />}
    </section>
  );
};

const EmailModal = ({ data, onClose, onChanged }) => {
  const [form, setForm] = useState(() => toForm(data.config));
  const [clearPassword, setClearPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [saved, setSaved] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [presetHelp, setPresetHelp] = useState('');

  const set = (k, v) => { setSaved(false); setForm((f) => ({ ...f, [k]: v })); };
  const passwordSet = data.config?.password_set && !clearPassword;

  const applyPreset = (key) => {
    const p = data.presets.find((x) => x.key === key);
    if (!p) return;
    setSaved(false);
    setForm((f) => ({ ...f, smtp_host: p.smtp_host || f.smtp_host, smtp_port: p.smtp_port, security: p.security }));
    setPresetHelp(p.help);
  };

  const save = async () => {
    setSaving(true);
    setErrors([]);
    try {
      await settingsService.saveEmail({ ...form, smtp_port: form.smtp_port === '' ? null : Number(form.smtp_port), clear_password: clearPassword && !form.password });
      setForm((f) => ({ ...f, password: '' }));
      setClearPassword(false);
      setSaved(true);
      onChanged();
      return true;
    } catch (e) {
      setErrors(e.response?.data?.errors || [errText(e)]);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Tests the SAVED settings, so save first.
  const test = async () => {
    setTestResult(null);
    if (!(await save())) return;
    setTesting(true);
    try {
      setTestResult(await settingsService.testEmail(testTo));
      onChanged();
    } catch (e) {
      setTestResult({ success: false, error: errText(e) });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-deep/50 p-4 backdrop-blur-sm" onMouseDown={(e) => e.target === e.currentTarget && !saving && !testing && onClose()}>
      <div className="my-8 w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h2 className="text-lg font-bold text-brand-deep">Email settings</h2>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><XMarkIcon className="h-5 w-5" /></button>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5">
          {!data.encryption_ready && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              The server has no encryption key set (SECRETS_ENCRYPTION_KEY or AI_ENCRYPTION_KEY), so a password can’t be stored.
            </p>
          )}

          <Field label="Start from">
            <select className={inputCls} defaultValue="" onChange={(e) => applyPreset(e.target.value)}>
              <option value="" disabled>Choose your email provider…</option>
              {data.presets.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            {presetHelp && <span className="text-xs text-slate-500">{presetHelp}</span>}
          </Field>

          <div className="grid grid-cols-6 gap-3">
            <Field label="SMTP server" className="col-span-4"><input className={inputCls} value={form.smtp_host} onChange={(e) => set('smtp_host', e.target.value)} placeholder="smtp.example.com" /></Field>
            <Field label="Port" className="col-span-2"><input className={inputCls} value={form.smtp_port} onChange={(e) => set('smtp_port', e.target.value.replace(/\D/g, ''))} /></Field>
            <Field label="Security" className="col-span-6">
              <select className={inputCls} value={form.security} onChange={(e) => set('security', e.target.value)}>
                {SECURITY.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Username" className="col-span-3"><input className={inputCls} value={form.username} onChange={(e) => set('username', e.target.value)} autoComplete="off" /></Field>
            <Field label={passwordSet ? 'Password (stored — type to replace)' : 'Password'} className="col-span-3">
              <input
                className={inputCls}
                type="password"
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
                placeholder={passwordSet ? '••••••••' : ''}
                autoComplete="new-password"
              />
              {data.config?.password_set && !form.password && (
                <label className="flex items-center gap-1.5 text-xs text-slate-500">
                  <input type="checkbox" checked={clearPassword} onChange={(e) => { setSaved(false); setClearPassword(e.target.checked); }} />
                  Remove the stored password
                </label>
              )}
            </Field>
            <Field label="From name" className="col-span-3"><input className={inputCls} value={form.from_name} onChange={(e) => set('from_name', e.target.value)} placeholder="e.g. Sandton Anaesthetists" /></Field>
            <Field label="From address" className="col-span-3"><input className={inputCls} type="email" value={form.from_address} onChange={(e) => set('from_address', e.target.value)} placeholder="accounts@practice.co.za" /></Field>
            <Field label="Reply-to (optional)" className="col-span-6"><input className={inputCls} type="email" value={form.reply_to} onChange={(e) => set('reply_to', e.target.value)} /></Field>
          </div>

          <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3">
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-brand-navy" checked={form.is_enabled} onChange={(e) => set('is_enabled', e.target.checked)} />
            <span>
              <span className="block text-sm font-semibold text-slate-700">Send email from OmniPrac</span>
              <span className="block text-xs text-slate-500">When off, nothing is sent (a test can still be sent from here).</span>
            </span>
          </label>

          <div className="rounded-lg bg-slate-50 p-3">
            <p className="mb-2 text-sm font-semibold text-slate-700">Send a test</p>
            <div className="flex gap-2">
              <input className={inputCls} type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@practice.co.za" />
              <button onClick={test} disabled={testing || saving || !testTo.trim()} className="shrink-0 rounded-lg border border-brand-navy px-4 text-sm font-semibold text-brand-navy hover:bg-white disabled:opacity-40">
                {testing ? 'Sending…' : 'Save & send test'}
              </button>
            </div>
            {testResult && (
              <p className={`mt-2 flex items-start gap-1 text-sm ${testResult.success ? 'text-green-700' : 'text-red-600'}`}>
                {testResult.success ? <CheckCircleIcon className="h-5 w-5 shrink-0" /> : <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />}
                {testResult.success ? 'Sent — the mail server accepted it. Check the inbox (and spam folder).' : testResult.error}
              </p>
            )}
          </div>

          {errors.length > 0 && <ul className="text-sm text-red-600">{errors.map((e) => <li key={e}>{e}</li>)}</ul>}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-6 py-3">
          {saved && <span className="mr-auto flex items-center gap-1 text-sm text-green-700"><CheckCircleIcon className="h-4 w-4" />Saved</span>}
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Close</button>
          <button onClick={save} disabled={saving} className="rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-60">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const inputCls = 'w-full rounded-lg border border-slate-300 p-2.5 text-sm outline-none transition-shadow focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40';

const Field = ({ label, className = '', children }) => (
  <label className={`flex flex-col gap-1 ${className}`}>
    <span className="text-[0.8rem] font-semibold text-slate-600">{label}</span>
    {children}
  </label>
);

export default EmailSettings;
